/**
 * NP-597 — placeholders for figures the server has not supplied yet.
 *
 * A monetary field rendered as "$0" is read as a commitment: a borrower cannot tell
 * "we have not calculated this" from "this is genuinely zero", and on Purchase Price
 * or Net Cash to Close that ambiguity is a legal exposure, not a cosmetic one. So an
 * absent figure reads "TBD" and only a confirmed zero reads "$0".
 *
 * Every page rendering money from Vesta goes through `formatMoney` — the rule lives
 * here rather than per page (AC-1, AC-12), so a new page inherits it.
 */

/** A monetary figure the server has not supplied or calculated yet (AC-2, AC-3). */
export const TBD = 'TBD';

/** A NON-monetary field with no value — Property Value, LTV (AC-5, AC-14). */
export const EM_DASH = '—';

/** Finite number or null; rejects undefined, NaN and the non-numeric wire cases. */
const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * Has this section's data arrived, so that a 0 in it can be believed?
 *
 * Vesta returns 0 both for "calculated, and it is zero" and for "not populated", and
 * the value alone cannot separate them — so the section decides. The rule is the
 * ticket's own: "if MOST or all sibling fields are also null/zero, treat all as TBD".
 *
 * Counted over the figures the API actually SENT, and "most" means more than half.
 * Both halves of that matter, because two real cases sit either side of the line:
 *
 *  - Scenario B sends every field, five of the six as 0 with one real Loan Amount.
 *    Most are zero, so the zeros are pending — the ticket's edge case says this one
 *    populated field is NOT enough to trust them.
 *  - A refinance (NP-584) sends only Purchase Price 0 and a real Loan Amount. Half
 *    are zero, not most, so that 0 is the fact it looks like: there is no purchase
 *    price. Counting absent fields as zeros would wrongly hide it.
 */
function sectionIsPopulated(siblings: ReadonlyArray<number | null | undefined>): boolean {
  const sent = siblings.map(finite).filter((value): value is number => value != null);
  if (sent.length === 0) return false;

  const zeros = sent.filter((value) => value === 0).length;
  return zeros * 2 <= sent.length;
}

/**
 * The one monetary formatter (AC-1).
 *
 * @param value    the figure, or null/undefined when the API did not send one.
 * @param siblings the other figures in the SAME section. Supplying them is what lets
 *                 a 0 among absent siblings read "TBD" (AC-4) while a 0 beside real
 *                 figures reads "$0" (AC-6). Omit for a standalone figure, where
 *                 there is no section to judge against and a 0 is taken at its word.
 *
 * Currency formatting is unchanged from what the app already renders — no decimals,
 * thousands separators — because the ticket's edge cases keep real values as they are
 * and only replace the whole formatted value when it is pending.
 */
export function formatMoney(
  value: number | null | undefined,
  siblings?: ReadonlyArray<number | null | undefined>,
): string {
  const amount = finite(value);
  if (amount == null) return TBD;
  if (amount === 0 && siblings && !sectionIsPopulated(siblings)) return TBD;

  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/**
 * A non-monetary value — a percentage, a count, a label (AC-5).
 *
 * `suffix` rides along so the unit is dropped with the value rather than left
 * stranded beside an em dash ("—%" reads as a rate of nothing).
 */
export function formatMetric(
  value: string | number | null | undefined,
  suffix = '',
): string {
  if (value == null || value === '') return EM_DASH;
  // A derived metric can arrive non-finite — LTV is loanAmount / propertyValue, so an
  // absent or zero property value yields NaN or Infinity. "NaN%" is worse than no
  // value at all, and formatMoney already rejects these (AC-2).
  if (typeof value === 'number' && !Number.isFinite(value)) return EM_DASH;
  return `${value}${suffix}`;
}
